import React, { useState, useEffect } from 'react';
import { T, TopNav, Footer, MonoLabel, BandBadge } from './ui';
import { getCandidateProgress } from '../data/progress';
import { supabase } from '../lib/supabase';

const CRITERIA = [
  { key:'ta',  field:'ta_score',  short:'TA',  label:'Task Response',              tip:'How well you address all parts of the task and develop a clear position.' },
  { key:'cc',  field:'cc_score',  short:'CC',  label:'Coherence & Cohesion',       tip:'Logical organisation, paragraph structure, and use of linking words.' },
  { key:'lr',  field:'lr_score',  short:'LR',  label:'Lexical Resource',           tip:'Range, accuracy, and appropriacy of vocabulary used.' },
  { key:'gra', field:'gra_score', short:'GRA', label:'Grammatical Range & Accuracy', tip:'Variety of sentence structures and accuracy of grammar.' },
];

function bandColor(n) {
  const v = parseFloat(n);
  if (!v) return '#9aa3b8';
  return v>=7.5?'#1a7a4a':v>=7?'#3a7a10':v>=6.5?'#b8860b':v>=6?'#b8620a':'#c0392b';
}

function bandLabel(n) {
  const v = parseFloat(n);
  if (!v) return null;
  return v>=7.5?'Strong':v>=6.5?'Good':v>=6?'Fair':'Needs work';
}

function avgField(rows, field) {
  const vals = rows.map(r => r[field]).filter(v => v != null && !isNaN(v));
  if (!vals.length) return null;
  return vals.reduce((a,b) => a+b, 0) / vals.length;
}

// Simple bar chart for essay history
function BandBarChart({ essays }) {
  if (!essays.length) return null;
  const items = essays.slice(-12);
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:6, height:72 }}>
      {items.map((e, i) => {
        const h = Math.max(8, ((parseFloat(e.band_score || e.overall)||0) / 9) * 72);
        const score = e.band_score ?? e.overall;
        return (
          <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
            <div style={{ fontSize:9, color:'#5a6a88', fontFamily:"'JetBrains Mono',monospace" }}>{parseFloat(score)?.toFixed(1)}</div>
            <div style={{ width:'100%', height:h, background:bandColor(score), borderRadius:'3px 3px 0 0', minHeight:8 }}/>
          </div>
        );
      })}
    </div>
  );
}

// Criterion trend sparkline (mini bar per essay)
function CriterionSparkline({ rows, field, color }) {
  const vals = rows.slice(-8).map(r => r[field]).filter(v => v != null);
  if (vals.length < 2) return null;
  const max = 9;
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:2, height:20 }}>
      {vals.map((v, i) => (
        <div key={i} style={{ flex:1, height:`${(v/max)*100}%`, minHeight:3, background:i===vals.length-1?color:`${color}66`, borderRadius:2, transition:'height .3s' }}/>
      ))}
    </div>
  );
}

export default function Dashboard({ branding, candidate, tiers, onBack, backLabel }) {
  const [supaScores, setSupaScores] = useState([]);

  // Fetch criterion scores from Supabase for academy students
  useEffect(() => {
    if (!candidate?.id) return;
    supabase.from('essay_scores')
      .select('band_score,ta_score,cc_score,lr_score,gra_score,submitted_at,task_type')
      .eq('student_id', candidate.id)
      .order('submitted_at', { ascending: true })
      .limit(50)
      .then(({ data }) => { if (data?.length) setSupaScores(data); });
  }, [candidate?.id]);

  const progress    = getCandidateProgress(candidate.email);
  const localEssays = progress.essays || [];
  const tier        = tiers[candidate.tier] || tiers.trial;
  const usedSmart   = candidate.usedSmart || 0;
  const limitSmart  = tier.limitSmart ?? tier.limit ?? 1;
  const remaining   = limitSmart >= 9999 ? 'Unlimited' : Math.max(0, limitSmart - usedSmart);

  // Use Supabase data for academy students, local for legacy
  const allScores   = supaScores.length > 0 ? supaScores : localEssays.map(e => ({ band_score: e.overall }));
  const hasData     = allScores.length > 0;
  const hasCriteria = supaScores.length > 0 && supaScores.some(s => s.ta_score != null);

  const avgBand = hasData
    ? (allScores.reduce((a, e) => a + (parseFloat(e.band_score||e.overall)||0), 0) / allScores.length).toFixed(1)
    : '—';

  const current  = allScores[allScores.length - 1] || null;
  const first    = allScores[0] || null;
  const best     = allScores.reduce((b, s) => (!b || (parseFloat(s.band_score||s.overall)||0) > (parseFloat(b.band_score||b.overall)||0)) ? s : b, null);

  // Per-criterion averages (last 5 essays)
  const recent5    = supaScores.slice(-5);
  const criteriaAvg = CRITERIA.map(c => ({
    ...c,
    avg:     avgField(supaScores, c.field),
    recent:  avgField(recent5, c.field),
    latest:  current?.[c.field] ?? null,
    first:   first?.[c.field] ?? null,
    trend:   (current?.[c.field] != null && first?.[c.field] != null)
               ? current[c.field] - first[c.field]
               : null,
  }));

  // Strength = highest avg criterion, weakness = lowest
  const ranked = criteriaAvg.filter(c => c.avg != null).sort((a,b) => b.avg - a.avg);
  const strength  = ranked[0] || null;
  const weakness  = ranked[ranked.length - 1] || null;

  return (
    <div style={{ minHeight:'100vh', background:'#f0f4f8', display:'flex', flexDirection:'column' }}>
      <TopNav branding={branding} onBack={onBack} backLabel={backLabel || 'Back'}/>

      <div style={{ maxWidth:900, margin:'0 auto', padding:'24px 20px', flex:1, width:'100%' }}>

        {/* Title */}
        <MonoLabel color='#b8860b' style={{ marginBottom:6 }}>My Writing Progress Tracker</MonoLabel>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, color:'#1a2a4a', marginBottom:20, fontWeight:700 }}>
          {candidate.name || candidate.email.split('@')[0]}
        </h1>

        {/* Stats row */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:12, marginBottom:24 }}>
          {[
            ['Essays Submitted', allScores.length || (usedSmart + (candidate.usedTest||0)), '#1a5fa8'],
            ['Essays Remaining', remaining, '#1a7a4a'],
            ['Average Band',     avgBand,   '#b8860b'],
            ['Best Score',       best ? parseFloat(best.band_score??best.overall)?.toFixed(1) : '—', '#5a1a8a'],
          ].map(([label, value, color]) => (
            <div key={label} style={{ background:'#fff', border:'1px solid #dde3ed', borderRadius:10, padding:'16px', textAlign:'center', boxShadow:'0 1px 4px rgba(26,42,74,0.06)' }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:30, fontWeight:700, color, lineHeight:1, marginBottom:4 }}>{value}</div>
              <div style={{ fontSize:11, color:'#9aa3b8', fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.06em', textTransform:'uppercase' }}>{label}</div>
            </div>
          ))}
        </div>

        {!hasData ? (
          <div style={{ background:'#fff', border:'1px solid #dde3ed', borderRadius:12, padding:'48px 24px', textAlign:'center', boxShadow:'0 1px 4px rgba(26,42,74,0.06)' }}>
            <div style={{ fontSize:40, marginBottom:14 }}>✍️</div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, color:'#1a2a4a', marginBottom:8 }}>No essays yet</div>
            <div style={{ fontSize:13.5, color:'#9aa3b8', lineHeight:1.7, maxWidth:380, margin:'0 auto' }}>
              Complete your first Writing Task 2 essay using the AI tool to start tracking your band score and criterion progress.
            </div>
          </div>
        ) : (
          <>
            {/* Band score history */}
            <div style={{ background:'#fff', border:'1px solid #dde3ed', borderRadius:12, padding:'20px', marginBottom:16, boxShadow:'0 1px 4px rgba(26,42,74,0.06)' }}>
              <div style={{ fontWeight:600, color:'#1a2233', marginBottom:14, fontSize:14 }}>
                Overall Band Score — Last {Math.min(allScores.length, 12)} Essays
              </div>
              <BandBarChart essays={allScores}/>
              <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
                <span style={{ fontSize:11, color:'#9aa3b8' }}>Oldest</span>
                <span style={{ fontSize:11, color:'#9aa3b8' }}>Most recent</span>
              </div>
            </div>

            {/* ── BAND DESCRIPTOR TRACKING ── */}
            {hasCriteria && (
              <div style={{ background:'#fff', border:'1px solid #dde3ed', borderRadius:12, padding:'22px', marginBottom:16, boxShadow:'0 1px 4px rgba(26,42,74,0.06)' }}>
                <div style={{ fontWeight:700, color:'#1a2233', marginBottom:4, fontSize:15 }}>Band Descriptors — Your Writing Profile</div>
                <div style={{ fontSize:13, color:'#5a6a88', marginBottom:20 }}>
                  Based on {supaScores.length} essay{supaScores.length !== 1 ? 's' : ''}. Scores out of 9.
                </div>

                {/* Strength / weakness callouts */}
                {(strength || weakness) && (
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:22 }}>
                    {strength && (
                      <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'14px 16px' }}>
                        <div style={{ fontSize:10, fontWeight:700, color:'#16a34a', fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.1em', marginBottom:4 }}>
                          ✓ YOUR STRENGTH
                        </div>
                        <div style={{ fontWeight:700, fontSize:14, color:'#1a2a4a' }}>{strength.label}</div>
                        <div style={{ fontSize:12, color:'#5a6a88', marginTop:2 }}>
                          Average: <span style={{ fontWeight:700, color:'#16a34a' }}>{strength.avg?.toFixed(1)}</span>
                        </div>
                      </div>
                    )}
                    {weakness && weakness.key !== strength?.key && (
                      <div style={{ background:'#fff8e8', border:'1px solid #f0d890', borderRadius:10, padding:'14px 16px' }}>
                        <div style={{ fontSize:10, fontWeight:700, color:'#b8860b', fontFamily:"'JetBrains Mono',monospace", letterSpacing:'.1em', marginBottom:4 }}>
                          ⚠ FOCUS AREA
                        </div>
                        <div style={{ fontWeight:700, fontSize:14, color:'#1a2a4a' }}>{weakness.label}</div>
                        <div style={{ fontSize:12, color:'#5a6a88', marginTop:2 }}>
                          Average: <span style={{ fontWeight:700, color:'#b8860b' }}>{weakness.avg?.toFixed(1)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Per-criterion rows */}
                {criteriaAvg.map(c => {
                  const color = bandColor(c.avg);
                  const pct   = ((c.avg || 0) / 9) * 100;
                  return (
                    <div key={c.key} style={{ marginBottom:18 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                        <div>
                          <span style={{ fontWeight:600, fontSize:13, color:'#1a2233' }}>{c.label}</span>
                          <span style={{ fontSize:11, color:'#9aa3b8', marginLeft:8, fontFamily:"'JetBrains Mono',monospace" }}>{c.short}</span>
                          {c.avg != null && (
                            <span style={{ marginLeft:8, fontSize:11, fontWeight:600, color:bandColor(c.avg), background:`${bandColor(c.avg)}18`, border:`1px solid ${bandColor(c.avg)}44`, borderRadius:100, padding:'1px 7px' }}>
                              {bandLabel(c.avg)}
                            </span>
                          )}
                        </div>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          {c.trend !== null && (
                            <span style={{ fontSize:12, fontWeight:700, color:c.trend>0.1?'#16a34a':c.trend<-0.1?'#c0392b':'#9aa3b8' }}>
                              {c.trend>0.1?'↑':c.trend<-0.1?'↓':'→'} {c.trend>0?'+':''}{c.trend.toFixed(1)}
                            </span>
                          )}
                          <span style={{ fontSize:15, fontWeight:700, color, fontFamily:"'JetBrains Mono',monospace", minWidth:32, textAlign:'right' }}>
                            {c.avg?.toFixed(1) ?? '—'}
                          </span>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div style={{ background:'#f0f4f8', borderRadius:100, height:10, overflow:'hidden', marginBottom:6 }}>
                        <div style={{ width:`${pct}%`, height:'100%', background:color, borderRadius:100, transition:'width .5s ease' }}/>
                      </div>
                      {/* Sparkline trend */}
                      {supaScores.length >= 2 && (
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ flex:1 }}>
                            <CriterionSparkline rows={supaScores} field={c.field} color={color}/>
                          </div>
                          <div style={{ fontSize:10, color:'#9aa3b8', whiteSpace:'nowrap' }}>
                            {c.first?.toFixed(1)} → {c.latest?.toFixed(1)}
                          </div>
                        </div>
                      )}
                      {/* Tooltip tip */}
                      <div style={{ fontSize:11.5, color:'#9aa3b8', marginTop:4, lineHeight:1.5 }}>{c.tip}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Recent essays (localStorage data) */}
            {localEssays.length > 0 && (
              <div style={{ background:'#fff', border:'1px solid #dde3ed', borderRadius:12, padding:'20px', boxShadow:'0 1px 4px rgba(26,42,74,0.06)' }}>
                <div style={{ fontWeight:600, color:'#1a2233', marginBottom:14, fontSize:14 }}>Recent Essays</div>
                {[...localEssays].reverse().slice(0,5).map((e,i,arr) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 0', borderBottom:i<arr.length-1?'1px solid #f0f4f8':'none' }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:500, color:'#1a2233', marginBottom:2 }}>{e.promptTitle}</div>
                      <div style={{ fontSize:11, color:'#9aa3b8', fontFamily:"'JetBrains Mono',monospace" }}>{e.date} · {e.essayType}</div>
                    </div>
                    <BandBadge band={e.overall}/>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <Footer branding={branding}/>
    </div>
  );
}
